import { useEffect, useState } from "react";
import { Check, Copy, Eye, EyeOff, Loader2 } from "lucide-react";
import { useCopyField, useRevealField } from "../hooks/useVault";
import type { RevealableField } from "../types";

const REVEAL_SECONDS = 30;
const COPY_SECONDS = 30;

type Props = {
  label: string;
  vaultId: string;
  field: RevealableField;
  hasValue: boolean;
  isPasswordType?: boolean;
  isEditMode?: boolean;
  editValue?: string;
  onEditChange?: (val: string) => void;
};

export function CredentialField({
  label,
  vaultId,
  field,
  hasValue,
  isPasswordType = false,
  isEditMode = false,
  editValue = "",
  onEditChange,
}: Props) {
  const reveal = useRevealField(vaultId);
  const copy = useCopyField(vaultId);
  const [revealedValue, setRevealedValue] = useState<string | null>(null);
  const [countdown, setCountdown] = useState(0);
  const [copyCountdown, setCopyCountdown] = useState(0);
  const [copied, setCopied] = useState(false);
  const [showPasswordEdit, setShowPasswordEdit] = useState(false);

  useEffect(() => {
    if (countdown <= 0) {
      if (revealedValue !== null) setRevealedValue(null);
      return;
    }
    const t = window.setTimeout(() => setCountdown((c) => c - 1), 1000);
    return () => window.clearTimeout(t);
  }, [countdown, revealedValue]);

  useEffect(() => {
    if (copyCountdown <= 0) return;
    const t = window.setTimeout(() => setCopyCountdown((c) => c - 1), 1000);
    return () => window.clearTimeout(t);
  }, [copyCountdown]);

  useEffect(() => {
    if (!copied) return;
    const t = window.setTimeout(() => setCopied(false), 2000);
    return () => window.clearTimeout(t);
  }, [copied]);

  async function handleReveal() {
    if (countdown > 0) {
      setRevealedValue(null);
      setCountdown(0);
      return;
    }
    try {
      const res = await reveal.mutateAsync(field);
      setRevealedValue(res.value);
      setCountdown(REVEAL_SECONDS);
    } catch {
      /* error toast handled elsewhere */
    }
  }

  async function handleCopy() {
    try {
      await copy.mutateAsync(field);
      setCopied(true);
      setCopyCountdown(COPY_SECONDS);
    } catch {
      /* swallow */
    }
  }

  if (isEditMode) {
    const inputType = isPasswordType && !showPasswordEdit ? "password" : "text";
    return (
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: "12px",
          padding: "8px 0",
        }}
      >
        <div
          style={{
            width: "120px",
            fontSize: "11px",
            textTransform: "uppercase",
            color: "hsl(215,16%,47%)",
            fontWeight: 500,
          }}
        >
          {label}
        </div>
        <div style={{ flex: 1, position: "relative" }}>
          <input
            type={inputType}
            name={`vlt-edit-${field}`}
            value={editValue}
            onChange={(e) => onEditChange?.(e.target.value)}
            placeholder={hasValue ? "••••• (unchanged)" : "Enter value"}
            // Block browser autofill on every credential edit input —
            // without these, Chrome injects the user's saved login
            // password into the password-type field. honeypot pair lives
            // in the parent VaultDetailPage's credentials card.
            autoComplete={isPasswordType ? "new-password" : "off"}
            data-lpignore="true"
            data-form-type="other"
            data-1p-ignore="true"
            style={{
              width: "100%",
              height: "32px",
              padding: isPasswordType ? "0 36px 0 8px" : "0 8px",
              border: "1px solid hsl(210,30%,85%)",
              borderRadius: "4px",
              fontSize: "13px",
              fontFamily: "monospace",
              background: "white",
            }}
          />
          {isPasswordType && (
            <button
              type="button"
              onClick={() => setShowPasswordEdit((v) => !v)}
              style={{
                position: "absolute",
                right: "6px",
                top: "50%",
                transform: "translateY(-50%)",
                background: "transparent",
                border: "none",
                cursor: "pointer",
                color: "hsl(215,16%,47%)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                padding: 4,
              }}
              title={showPasswordEdit ? "Hide" : "Show"}
            >
              {showPasswordEdit ? <EyeOff size={14} /> : <Eye size={14} />}
            </button>
          )}
        </div>
      </div>
    );
  }

  const isRevealing = reveal.isPending;
  const isCopying = copy.isPending;

  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: "12px",
        padding: "10px 0",
      }}
    >
      <div
        style={{
          width: "120px",
          fontSize: "11px",
          textTransform: "uppercase",
          color: "hsl(215,16%,47%)",
          fontWeight: 500,
        }}
      >
        {label}
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        {!hasValue ? (
          <span style={{ color: "hsl(215,16%,47%)" }}>—</span>
        ) : revealedValue !== null ? (
          <div
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: "8px",
              padding: "4px 8px",
              borderRadius: "4px",
              background: "hsl(142,50%,96%)",
              border: "1px solid hsl(142,50%,80%)",
              color: "hsl(142,72%,28%)",
              fontFamily: "monospace",
              fontSize: 13,
              maxWidth: "100%",
              overflow: "hidden",
            }}
          >
            <span style={{ wordBreak: "break-all" }}>{revealedValue}</span>
            {countdown > 0 && (
              <span
                style={{
                  fontSize: 10,
                  color: "hsl(142,40%,40%)",
                  whiteSpace: "nowrap",
                }}
              >
                Hiding in {countdown}s
              </span>
            )}
          </div>
        ) : (
          <span
            style={{
              fontFamily: "monospace",
              color: "hsl(215,16%,47%)",
              letterSpacing: "1px",
            }}
          >
            ••••••••••••
          </span>
        )}
      </div>
      {hasValue && (
        <div style={{ display: "flex", gap: "4px", alignItems: "center" }}>
          {copyCountdown > 0 && (
            <span
              style={{
                fontSize: 10,
                color: "hsl(215,16%,47%)",
                marginRight: 4,
              }}
            >
              Clipboard clears in {copyCountdown}s
            </span>
          )}
          <button
            type="button"
            onClick={handleReveal}
            disabled={isRevealing}
            style={{
              width: "26px",
              height: "26px",
              border: "1px solid hsl(210,30%,85%)",
              borderRadius: "4px",
              background: "white",
              cursor: isRevealing ? "wait" : "pointer",
              color: "hsl(215,16%,47%)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
            title={revealedValue !== null ? "Hide" : "Reveal"}
          >
            {isRevealing ? (
              <Loader2 size={12} className="animate-spin" />
            ) : revealedValue !== null ? (
              <EyeOff size={14} />
            ) : (
              <Eye size={14} />
            )}
          </button>
          <button
            type="button"
            onClick={handleCopy}
            disabled={isCopying}
            style={{
              width: "26px",
              height: "26px",
              border: "1px solid hsl(210,30%,85%)",
              borderRadius: "4px",
              background: "white",
              cursor: isCopying ? "wait" : "pointer",
              color: copied ? "hsl(142,72%,28%)" : "hsl(215,16%,47%)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
            title="Copy to clipboard"
          >
            {isCopying ? (
              <Loader2 size={12} className="animate-spin" />
            ) : copied ? (
              <Check size={14} />
            ) : (
              <Copy size={14} />
            )}
          </button>
        </div>
      )}
    </div>
  );
}
